// API configuration that works for preview, production, and custom domains
// Uses window.location.origin for deployed environments (handles any domain)
// Falls back to REACT_APP_BACKEND_URL only for local development

import axios from 'axios';

// Get the API base URL - called at runtime
const getBackendUrl = () => {
  // For any browser environment (production, preview, custom domains),
  // use window.location.origin — Emergent's ingress routes /api to the backend
  if (typeof window !== 'undefined' && window.location && window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  // In local development, use environment variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Fallback
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }
  return '';
};

// Export as getter to ensure runtime evaluation
export const getApiBaseUrl = getBackendUrl;
export const getApiUrl = () => `${getBackendUrl()}/api`;

// For backwards compatibility - these will be evaluated when imported
// but components should use getApiUrl() for dynamic resolution
export const BACKEND_URL = getBackendUrl();
export const API = `${getBackendUrl()}/api`;

// Create axios instance - will use the URL at creation time
// Components can also use getApiUrl() directly for dynamic calls
export const apiClient = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to set baseURL dynamically
apiClient.interceptors.request.use(
  (config) => {
    // Always use current API URL (handles production vs preview)
    config.baseURL = getApiUrl();
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorInfo = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
    };
    console.error('API Error:', errorInfo);

    if (error.response?.status === 429) {
      console.warn('Rate limited - API is temporarily unavailable');
    } else if (error.response?.status === 500) {
      console.error('Server error - please try again later');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - server may be overloaded');
    }

    return Promise.reject(error);
  }
);

export default API;
